/**
 * @file Sidebar 侧栏导航
 * @description 后台左侧壳；Menu + 中部内容区。收起时 w-16；hover 临时展开为浮层 w-56，占位宽度不变。
 *
 * @whenToUse Admin Layout 左侧导航。
 * @whenNotToUse 页内锚点目录（用 Tree）；操作浮层（用 DropdownMenu）。
 */

import { useEffect, useState, type ReactNode } from 'react'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Button } from '@/components/ui/button.tsx'
import { Menu, type MenuItem } from '@/components/ui/menu.tsx'

/** 侧栏展开宽度（px，对应 w-56） */
const SIDEBAR_WIDTH_EXPANDED = 256

/** 侧栏收起占位宽度（px，对应 w-16） */
const SIDEBAR_WIDTH_COLLAPSED = 64

export type SidebarItem = {
  key: string
  label: ReactNode
  to?: string
  icon?: ReactNode
  hidden?: boolean
  children?: SidebarItem[]
  disabled?: boolean
}

export type SidebarProps = {
  items: SidebarItem[]
  /**
   * 品牌区内容；可传入节点，或按折叠态渲染（收起时通常只留 logo）。
   */
  brand?: ReactNode | ((collapsed: boolean) => ReactNode)
  /** 是否折叠为仅图标（传给 Menu.collapsed） */
  collapsed?: boolean
  /** 折叠变化；传入时在侧栏最底部显示收起/展开按钮 */
  onCollapsedChange?: (collapsed: boolean) => void
  /** 底部区域 */
  footer?: ReactNode
  className?: string
}

function toMenuItems(items: SidebarItem[]): MenuItem[] {
  return items.filter((item) => !item.hidden).map((item) => ({
    key: item.key,
    label: item.label,
    to: item.to,
    icon: item.icon,
    disabled: item.disabled,
    type: item.children?.length ? 'group' : 'item',
    children: item.children ? toMenuItems(item.children) : undefined,
  }))
}

export function Sidebar({
  items,
  brand,
  collapsed = false,
  onCollapsedChange,
  footer,
  className,
}: SidebarProps) {
  const [hoverExpanded, setHoverExpanded] = useState(false)
  const pinCollapsed = collapsed
  const menuCollapsed = pinCollapsed && !hoverExpanded
  const brandCollapsed = pinCollapsed && !hoverExpanded
  const brandNode =
    typeof brand === 'function' ? brand(brandCollapsed) : brand

  useEffect(() => {
    if (!pinCollapsed) setHoverExpanded(false)
  }, [pinCollapsed])

  return (
    <div
      className={cn(
        'relative h-full shrink-0',
        pinCollapsed ? 'w-16' : 'w-56',
        className,
      )}
      style={{
        width: pinCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
      }}
    >
      <aside
        className={cn(
          'flex h-full flex-col border-r border-border bg-sidebar transition-[width] duration-300',
          pinCollapsed && hoverExpanded && 'absolute left-0 top-0 z-50 w-56',
          (pinCollapsed && !hoverExpanded) || !pinCollapsed ? 'w-full' : null,
        )}
        style={
          pinCollapsed && hoverExpanded
            ? { width: SIDEBAR_WIDTH_EXPANDED }
            : undefined
        }
        onMouseEnter={() => {
          if (pinCollapsed) setHoverExpanded(true)
        }}
        onMouseLeave={() => {
          if (pinCollapsed) setHoverExpanded(false)
        }}
      >
        {brandNode ? (
          <div
            className={cn(
              'flex h-14 shrink-0 items-center border-b border-border px-3',
              brandCollapsed && 'justify-center px-2',
            )}
          >
            {brandNode}
          </div>
        ) : null}
        <nav className="min-h-0 flex-1 overflow-y-auto p-2">
          <Menu
            mode="vertical"
            items={toMenuItems(items)}
            collapsed={menuCollapsed}
            onCollapsedChange={onCollapsedChange}
          />
        </nav>
        {footer || onCollapsedChange ? (
          <div
            className={cn(
              'flex shrink-0 flex-col gap-2 border-t border-border p-2',
              menuCollapsed && 'items-center',
            )}
          >
            {footer}
            {onCollapsedChange ? (
              <Button
                variant="outline"
                aria-label={pinCollapsed ? '展开侧栏' : '折叠侧栏'}
                className="overflow-hidden whitespace-nowrap"
                icon={
                  pinCollapsed ? (
                    <ChevronsRight className="size-4" aria-hidden />
                  ) : (
                    <>
                      <ChevronsLeft className="size-4" aria-hidden />
                      <span>收起侧栏</span>
                    </>
                  )
                }
                onClick={() => onCollapsedChange(!pinCollapsed)}
              />
            ) : null}
          </div>
        ) : null}
      </aside>
    </div>
  )
}
